/**
 * Timer host abstraction so every scheduler in the phone can run against vitest fake timers,
 * plus a wall-clock deadline timer. Chrome may wake a throttled page up to 60 s late: a
 * DeadlineTimer therefore fires on the server-relative wall clock, not on elapsed timer time.
 */
import type { Clock } from '../app/clock.js';

export interface TimerHost {
  setTimeout(fn: () => void, ms: number): unknown;
  clearTimeout(handle: unknown): void;
  setInterval(fn: () => void, ms: number): unknown;
  clearInterval(handle: unknown): void;
}

export const realTimers: TimerHost = {
  setTimeout: (fn, ms) => globalThis.setTimeout(fn, ms),
  clearTimeout: (h) => globalThis.clearTimeout(h as ReturnType<typeof setTimeout>),
  setInterval: (fn, ms) => globalThis.setInterval(fn, ms),
  clearInterval: (h) => globalThis.clearInterval(h as ReturnType<typeof setInterval>),
};

/** Fires `fn` once the clock passes `deadlineMs`; a late wake fires immediately, an early wake re-arms. */
export class DeadlineTimer {
  private handle: unknown = null;
  private deadline: number | null = null;

  constructor(
    private readonly clock: Clock,
    private readonly timers: TimerHost = realTimers,
    /** upper bound on one sleep so a clock jump is noticed */
    private readonly maxSliceMs = 30_000,
  ) {}

  arm(deadlineMs: number, fn: () => void): void {
    this.cancel();
    this.deadline = deadlineMs;
    const tick = (): void => {
      const remaining = deadlineMs - this.clock.now();
      if (remaining <= 0) {
        this.handle = null;
        this.deadline = null;
        fn();
        return;
      }
      this.handle = this.timers.setTimeout(tick, Math.min(remaining, this.maxSliceMs));
    };
    tick();
  }

  /** milliseconds left, or null when idle */
  remaining(): number | null {
    return this.deadline === null ? null : Math.max(0, this.deadline - this.clock.now());
  }

  get armed(): boolean {
    return this.deadline !== null;
  }

  cancel(): void {
    if (this.handle !== null) this.timers.clearTimeout(this.handle);
    this.handle = null;
    this.deadline = null;
  }
}

/** Promise that resolves after `ms` on the given host; rejects with `reason` when `signal` aborts. */
export function sleep(ms: number, timers: TimerHost = realTimers): Promise<void> {
  return new Promise((resolve) => {
    timers.setTimeout(resolve, ms);
  });
}

/** Rejects a promise after `ms`; every network and media call in the phone goes through this. */
export function withTimeout<T>(p: Promise<T>, ms: number, what: string, timers: TimerHost = realTimers): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const h = timers.setTimeout(() => reject(new TimeoutError(what, ms)), ms);
    p.then(
      (v) => {
        timers.clearTimeout(h);
        resolve(v);
      },
      (e: unknown) => {
        timers.clearTimeout(h);
        reject(e);
      },
    );
  });
}

export class TimeoutError extends Error {
  constructor(
    readonly what: string,
    readonly ms: number,
  ) {
    super(`${what} timed out after ${ms} ms`);
    this.name = 'TimeoutError';
  }
}

export function formatClock(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}
